import React, { useMemo, useState, useEffect } from 'react';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';

import { FlexColumn } from 'src/components/flex';
import Category from 'src/pages/landlord/conversations/components/list/ConversationsListCategory';
import NoSearchResults from 'src/pages/landlord/conversations/components/list/ConversationsNoSearchResults';
import type { EConversationCategory, IConversationMatchWithChannel, EConversationTab } from 'src/pages/landlord/conversations/context/types';
import { conversationCategories, initialConversationsPagesByCategory } from 'src/pages/landlord/conversations/context/types';
import type { IConversation } from 'src/api/landlord-messages-api';
import type { TConversationsFilter } from 'src/services/api/messages/use-conversations-search';
import { useAuthenticatedState } from 'src/authenticated-state/context';

type Props = {
    tab?: EConversationTab;
    search: string;
    filter: TConversationsFilter;
    conversationsByCategory: Record<EConversationCategory, IConversation[]>;
    selectedConversations: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations: React.Dispatch<React.SetStateAction<Array<IConversation | IConversationMatchWithChannel>>>;
    focusedConversation?: IConversation;
    setFocusedConversation?: (conversation: IConversation | undefined) => void;
    setMobileMenuAnchorEl: (anchorEl: HTMLElement | undefined) => void;
};

const ROWS_PER_PAGE = 25;

const ConversationsList = ({
    search: searchQuery,
    filter: searchFilter,
    conversationsByCategory,
    selectedConversations,
    setSelectedConversations,
    focusedConversation,
    setFocusedConversation,
    setMobileMenuAnchorEl,
}: Props) => {
    const navigate = useNavigate();

    // Get data for UI logic
    const { showArchived, leadsByUuid } = useAuthenticatedState();

    // Local pagination state
    const [pagesByCategory, setPagesByCategory] = useState(initialConversationsPagesByCategory);

    // Compute categories based on showArchived
    const categories = useMemo(() => {
        if (showArchived) {
            return conversationCategories;
        } else {
            return conversationCategories.filter(o => o !== 'Archived');
        }
    }, [showArchived]);

    // Count total filtered conversations across all categories
    const totalFilteredConversations = useMemo(() => {
        return conversationCategories.reduce((sum, cat) => {
            return sum + conversationsByCategory[cat].length;
        }, 0);
    }, [conversationsByCategory]);

    // Compute visible conversations by category (with pagination)
    const visibleConversationsByCategory = useMemo(() => {
        return conversationCategories.reduce((acc, cat) => {
            const _conversations = conversationsByCategory[cat];
            const _page = pagesByCategory[cat];
            return {
                ...acc,
                [cat]: _conversations.slice((_page - 1) * ROWS_PER_PAGE, _page * ROWS_PER_PAGE),
            };
        }, {} as Record<EConversationCategory, IConversation[]>);
    }, [conversationsByCategory, pagesByCategory]);

    // Reset pages when conversations by category changes
    useEffect(() => {
        setPagesByCategory(initialConversationsPagesByCategory);
    }, [totalFilteredConversations]);

    // Check if search or filter is active
    const isSearchOrFilterActive = !!searchQuery ||
        !!searchFilter?.assigneeIds?.length ||
        !!searchFilter?.ingestionSources?.length ||
        !!searchFilter?.leadStages?.length ||
        !!searchFilter?.leadLabelIds?.length ||
        !!searchFilter?.propertyIds?.length ||
        !!searchFilter?.propertyLabelIds?.length;

    const isSearchingWithoutResults = isSearchOrFilterActive && totalFilteredConversations === 0;

    return (
        <FlexColumn gap={2} py={3} alignItems="center">
            {!isSearchingWithoutResults && categories.map((cat) => (
                <Category
                    key={cat}
                    category={cat}
                    conversationsByCategory={conversationsByCategory}
                    visibleConversationsByCategory={visibleConversationsByCategory}
                    leadsByUuid={leadsByUuid!}
                    selectedConversations={selectedConversations}
                    setSelectedConversations={setSelectedConversations}
                    focusedConversation={focusedConversation}
                    setFocusedConversation={setFocusedConversation}
                    rowsPerPage={ROWS_PER_PAGE}
                    pagesByCategory={pagesByCategory}
                    setPagesByCategory={setPagesByCategory}
                    setMobileMenuAnchorEl={setMobileMenuAnchorEl}
                />
            ))}

            {isSearchingWithoutResults && (
                <NoSearchResults />
            )}

            {/* only show button if the user is searching */}
            {isSearchOrFilterActive && (
                <Button
                    role="link"
                    variant="contained"
                    onClick={() => {
                        const params = new URLSearchParams();
                        if (searchFilter) {
                            for (const [key, value] of Object.entries(searchFilter)) {
                                if (value && !(Array.isArray(value) && value.length === 0)) {
                                    params.append(key, String(value));
                                }
                            }
                        }
                        if (searchQuery) {
                            params.append('q', searchQuery || '');
                        }
                        const path = '/landlord/inner/archive?' + params.toString();

                        navigate(path);
                    }}
                    startIcon={<ArchiveIcon />}
                    data-testid="search-archived-btn"
                >
                    Search In Archive
                </Button>
            )}
        </FlexColumn >
    );
};

export default ConversationsList;
